/**
 * The practice yard — a small walled scrapyard the new survivor is dropped
 * into before the settlement. Authored in code (not a baked map file) because
 * every position here is also a rule: the lesson order runs south to north, and
 * the view reads the same constants for its markers, targets and crate.
 */
import type { Vec2, WorldData, WorldObject } from '../types';

/** Where the player appears. */
export const YARD_SPAWN: Vec2 = { x: 0, z: 14 };
/** Step 1 target — a short walk straight ahead. */
export const MOVE_MARKER: Vec2 = { x: 0, z: 6 };
/** How close counts as "reached" the marker. */
export const MARKER_RADIUS = 1.3;
/** Step 2 — one dummy, close enough to stab. */
export const MELEE_DUMMY: Vec2 = { x: -6, z: 0 };
/** Step 3 — three targets at range. */
export const SHOOT_TARGETS: readonly Vec2[] = [
  { x: -3, z: -12 }, { x: 0, z: -13 }, { x: 3, z: -12 },
];
/** Step 4 — the crate. */
export const CRATE_POS: Vec2 = { x: 8, z: -2 };
export const CRATE_ID = 'tutorial-crate';
export const DUMMY_RADIUS = 0.45;
export const MELEE_DUMMY_ID = 'dummy-melee';
export const shootTargetId = (i: number): string => `dummy-shoot-${i}`;

const STRAW = 0xb89a5a;
const WALL = 0x4a4238;

function wall(id: string, x: number, z: number, w: number, d: number, h = 2.6): WorldObject {
  return {
    // 'prop', not 'building': a building gets a roof sized from its footprint,
    // and a 30 m wall grew a 51 m roof that hid the whole yard.
    id, kind: 'prop', label: 'Scrap wall', position: { x, z }, size: { w, d, h }, color: WALL, interactable: false,
  };
}

export function buildTutorialWorld(): WorldData {
  const dummy = (id: string, p: Vec2, label: string): WorldObject => ({
    id, kind: 'prop', label, position: p, size: { w: DUMMY_RADIUS * 2, d: DUMMY_RADIUS * 2, h: 1.7 },
    color: STRAW, round: true, interactable: false,
  });
  const objects: WorldObject[] = [
    // The yard's boundary, so the player cannot wander off the lesson.
    wall('wall-n', 0, -17.5, 30, 1), wall('wall-s', 0, 18.5, 30, 1),
    wall('wall-w', -15.5, 0, 1, 36), wall('wall-e', 15.5, 0, 1, 36),
    // Dressing: a few barrels and a wreck so it reads as a scrapyard.
    {
      id: 'barrel-1', kind: 'prop', label: 'Barrel', position: { x: 10, z: 8 }, size: { w: 0.8, d: 0.8, h: 1 },
      color: 0x6a3a22, round: true, interactable: false,
    },
    {
      id: 'barrel-2', kind: 'prop', label: 'Barrel', position: { x: -10, z: 9 }, size: { w: 0.8, d: 0.8, h: 1 },
      color: 0x3a4a3a, round: true, interactable: false,
    },
    {
      id: 'wreck', kind: 'prop', label: 'Wrecked car', position: { x: -9, z: 13 }, size: { w: 4.2, d: 2, h: 1.4 },
      color: 0x5a4a3a, interactable: false, rotationY: 0.4,
    },
    // Step 1 marker: flat, walk-through.
    {
      id: 'marker-move', kind: 'prop', label: 'Go here', position: MOVE_MARKER, size: { w: 2.4, d: 2.4, h: 0.06 },
      color: 0x5fae5a, round: true, interactable: false, noClip: true,
    },
    dummy(MELEE_DUMMY_ID, MELEE_DUMMY, 'Straw dummy'),
    ...SHOOT_TARGETS.map((p, i) => dummy(shootTargetId(i), p, 'Target')),
    {
      id: CRATE_ID, kind: 'loot', label: 'Supply crate', position: CRATE_POS, size: { w: 1.1, d: 0.9, h: 0.8 },
      color: 0x8a6a34, interactable: true, lootObjectId: CRATE_ID,
    },
  ];
  return {
    cityName: 'Practice Yard',
    ground: { width: 30, depth: 36 },
    spawnPoint: YARD_SPAWN,
    objects,
    npcs: {},
    dialog: {},
    // The settlement's ground, so the yard reads as the same world.
    groundTexture: {
      textureUrl: '/textures/ground-realistic.webp', textureScale: 0.17, textureRotation: 0,
      secondaryUrl: '/textures/ground-stylized.webp', secondaryScale: 0.35, secondaryRotation: 0.5,
      noiseUrl: '/textures/noise-detailed.webp', noiseScale: 0.06, noiseRotation: -0.37, noiseOffset: 0.08, noiseGain: 0.81,
    },
    // Dusk, but lifted: a lesson has to be readable, and with no campfire the
    // shipped light leaves the dummies and the crate nearly black.
    lighting: { fill: 1.1, key: 1.3, campfire: 0, fillGround: 0x4a3a2a },
  };
}
